import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuditLogs, getFinancialRecords, createFinancialRecord, updateFinancialRecord } from '../api';
import './AccountsWorkspace.css';

const AccountsWorkspace = () => {
  // Estados de Seguridad (Solo dependemos del JWT ahora)
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Estados de Datos
  const [activeTab, setActiveTab] = useState('audit');
  const [auditLogs, setAuditLogs] = useState([]);
  const [financialRecords, setFinancialRecords] = useState([]);
  
  const [newRecord, setNewRecord] = useState({
    date: new Date().toISOString().split('T')[0],
    concept: '',
    amount: '',
    voucher_link: '',
    notes: ''
  });

  // 1. Verificar si tiene token JWT válido (Admin o Founder)
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    if (token && (role === 'admin' || role === 'founder')) {
      setIsAuthorized(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 2. Descargar datos SOLO si está autorizado
  const fetchData = useCallback(async () => {
    if (!isAuthorized) return;
    try {
      if (activeTab === 'audit') {
        const res = await getAuditLogs();
        setAuditLogs(res.data);
      } else {
        const res = await getFinancialRecords();
        setFinancialRecords(res.data);
      }
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }, [activeTab, isAuthorized]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Si no tiene token, lo mandamos al login general
  if (!isAuthorized && localStorage.getItem('token') === null) {
    return <Navigate to="/admin-studio-x99-bullminer" />;
  }

  const handleAddRecord = async (e) => {
    e.preventDefault();
    try {
      await createFinancialRecord({
        ...newRecord,
        amount: parseFloat(newRecord.amount)
      });
      setNewRecord({ ...newRecord, concept: '', amount: '', voucher_link: '', notes: '' });
      fetchData();
    } catch (err) {
      alert("Error al guardar registro financiero");
    }
  };

  const handleUpdateNotes = async (id, newNotes) => {
    try {
      await updateFinancialRecord(id, newNotes);
    } catch (err) {
      console.error("Error updating notes", err);
    }
  };

  const formatDate = (utcDateStr) => {
    return new Date(utcDateStr).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  };

  // =====================================================================
  // PANTALLA DE CUENTAS (Protegida por JWT)
  // =====================================================================
  return (
    <div className="accounts-container">
      <div className="accounts-header">
        <h1>Panel de Control y Finanzas</h1>
        <div className="tabs-container">
          <button className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
            <i className="fas fa-history"></i> Auditoría del Sistema
          </button>
          <button className={`tab-btn ${activeTab === 'finance' ? 'active' : ''}`} onClick={() => setActiveTab('finance')}>
            <i className="fas fa-file-invoice-dollar"></i> Registro Financiero
          </button>
        </div>
      </div>

      <div className="accounts-content">
        {activeTab === 'audit' && (
          <div className="table-wrapper">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>Fecha (UTC)</th>
                  <th>Usuario</th>
                  <th>Acción</th>
                  <th>Descripción</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map(log => (
                  <tr key={log.id}>
                    <td>{formatDate(log.created_at)}</td>
                    <td><span style={{color:'#58A6FF'}}>{log.user_identifier}</span></td>
                    <td><span style={{background:'#30363D', padding:'4px 8px', borderRadius:'4px', fontSize:'0.8rem'}}>{log.action}</span></td>
                    <td>{log.description}</td>
                  </tr>
                ))}
                {auditLogs.length === 0 && <tr><td colSpan="4" style={{textAlign:'center'}}>No hay registros de auditoría.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'finance' && (
          <>
            <form className="finance-form" onSubmit={handleAddRecord}>
              <div className="form-group-dash">
                <label>Fecha</label>
                <input type="date" value={newRecord.date} onChange={e => setNewRecord({...newRecord, date: e.target.value})} required />
              </div>
              <div className="form-group-dash" style={{flex: 2}}>
                <label>Concepto</label>
                <input type="text" placeholder="Ej: Pago Curso Python - Juan" value={newRecord.concept} onChange={e => setNewRecord({...newRecord, concept: e.target.value})} required />
              </div>
              <div className="form-group-dash">
                <label>Monto (USD)</label>
                <input type="number" step="0.01" placeholder="0.00" value={newRecord.amount} onChange={e => setNewRecord({...newRecord, amount: e.target.value})} required />
              </div>
              <div className="form-group-dash" style={{flex: 2}}>
                <label>Link Voucher (Drive)</label>
                <input type="text" placeholder="https://drive..." value={newRecord.voucher_link} onChange={e => setNewRecord({...newRecord, voucher_link: e.target.value})} />
              </div>
              <button type="submit" className="btn-save" style={{height: '40px', padding: '0 20px'}}>Registrar</button>
            </form>

            <div className="table-wrapper">
              <table className="accounts-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Concepto</th>
                    <th>Monto</th>
                    <th>Voucher</th>
                    <th>Registrado Por</th>
                    <th>Notas (Editable)</th>
                  </tr>
                </thead>
                <tbody>
                  {financialRecords.map(rec => (
                    <tr key={rec.id}>
                      <td>{rec.date}</td>
                      <td>{rec.concept}</td>
                      <td className="amount-cell">${rec.amount.toFixed(2)}</td>
                      <td>
                        {rec.voucher_link ? <a href={rec.voucher_link} target="_blank" rel="noopener noreferrer" className="link-drive"><i className="fas fa-external-link-alt"></i> Ver</a> : '-'}
                      </td>
                      <td>{rec.registered_by}</td>
                      <td>
                        <input 
                          type="text" 
                          className="editable-notes" 
                          defaultValue={rec.notes || ''} 
                          onBlur={(e) => handleUpdateNotes(rec.id, e.target.value)}
                          placeholder="Añadir nota..."
                        />
                      </td>
                    </tr>
                  ))}
                  {financialRecords.length === 0 && <tr><td colSpan="6" style={{textAlign:'center'}}>No hay registros financieros.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountsWorkspace;
